import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Award, BarChart3, TrendingUp, TrendingDown, Minus } from "lucide-react";

const getTrendIcon = (trend) => {
  if (trend === "improving") return <TrendingUp className="w-4 h-4 text-green-500" />;
  if (trend === "declining") return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-gray-500" />;
};

/**
 * Interview completion screen shown after a user finishes all questions.
 * Displays overall score, benchmarks, difficulty progression, and next-action buttons.
 */
export const InterviewFeedbackView = ({ session, benchmarks, onReset, onViewHistory }) => {
  if (!session) return null;
  const fb = session.overall_feedback || {};

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="border-0 shadow-xl bg-white/80 backdrop-blur">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl">
              <Award className="w-8 h-8 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl">Interview Complete! 🎉</CardTitle>
              <CardDescription>Here's your comprehensive performance analysis</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Overall Score */}
          <div className="text-center py-8 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 rounded-xl">
            <h3 className="text-6xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
              {Math.round(fb.average_score || 0)}
            </h3>
            <p className="text-xl font-medium text-gray-700">
              {fb.performance_level || "Good"}
            </p>
            <div className="flex items-center justify-center gap-2 mt-2">
              {getTrendIcon(fb.performance_trend)}
              <span className="text-sm text-gray-600">
                {fb.performance_trend === "improving" ? "Improving throughout" :
                 fb.performance_trend === "declining" ? "Consider more practice" :
                 "Consistent performance"}
              </span>
            </div>
          </div>

          {/* Benchmarks */}
          {benchmarks && benchmarks.length > 0 && (
            <div>
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-500" />
                Benchmark Comparison
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {benchmarks.slice(0, 4).map((b, i) => (
                  <div key={i} className="p-4 bg-gray-50 rounded-xl text-center">
                    <p className="text-3xl font-bold text-blue-600">{Math.round(b.user_score)}</p>
                    <p className="text-sm text-gray-600">{b.metric_name}</p>
                    <Badge variant={b.user_score >= b.average_score ? "default" : "secondary"} className="mt-2">
                      Top {b.percentile_rank}%
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Difficulty Progression */}
          {fb.difficulty_progression && (
            <div className="p-4 bg-purple-50 rounded-xl">
              <h4 className="font-semibold mb-2">Difficulty Progression</h4>
              <div className="flex gap-2 flex-wrap">
                {fb.difficulty_progression.map((d, i) => (
                  <Badge key={i} variant="outline" className={`
                    ${d === 'easy' ? 'bg-green-100 border-green-300' :
                      d === 'medium' ? 'bg-yellow-100 border-yellow-300' :
                      d === 'hard' ? 'bg-orange-100 border-orange-300' :
                      'bg-red-100 border-red-300'}
                  `}>
                    Q{i + 1}: {d}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <Button onClick={onReset} variant="outline" className="flex-1">
              Start New Interview
            </Button>
            <Button
              onClick={onViewHistory}
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600"
            >
              View All History
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InterviewFeedbackView;
